import { useCallback, useEffect, useId, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { checkGoal, goalSpec, mbKey, SCENES } from "../../microbit";
import type { MbGoalConfig, SceneId } from "../../microbit";
import { Conflict, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { CatalogItem, Issue, Json } from "../types";
import { fmtDate } from "./ConfigTab";
import { StagePicker } from "./StagePicker";
import type { StageProduct } from "./StagePicker";

/**
 * 무대 목표 — 마이크로비트 놀이터(2026-10-08 · FS-15-08 안) — 장면마다 별 둘 · 셋 기준과 문구를 바꾼다(카탈로그 mb_goal · key = 장면 키).
 *  - 별 하나는 오늘의 미션(Campus 판정)이라 바꾸지 않는다 — 보기만
 *  - 횟수 장면: ⭐ < ⭐⭐ < ⭐⭐⭐ ≤ 상한(장면이 세는 끝) · 끝이 있는 장면: ⭐⭐ 시간 > ⭐⭐⭐ 시간(더 빨리) · 균형: 버틴 시간
 *  - 초안 저장 → 배포(새로 여는 놀이터부터) · 이전 버전 배포 = 되돌리기 · '기본 기준으로' = 끄기
 * 마이크로비트 장면은 커널 판정이 아니라 화면 규칙(lib/sceneStars)이라 정답 시험 대신 학생이 볼 줄을 미리 보여 준다.
 */
const TYPE = "mb_goal";

interface Form {
  s2: string;
  s3: string;
  l2ko: string;
  l2en: string;
  l3ko: string;
  l3en: string;
}

const toUnit = (kind: string, v: number) => (kind === "count" ? String(v) : String(v / 1000));
const fromUnit = (kind: string, v: string) => {
  const n = Number(v);
  if (!Number.isFinite(n) || v.trim() === "") return NaN;
  return kind === "count" ? Math.round(n) : Math.round(n * 1000);
};

export function MbGoalsPanel({
  api,
  onProduct,
}: {
  api: OpsApi;
  onProduct: (p: StageProduct) => void;
}) {
  const t = useT();
  const id = useId();
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [scene, setScene] = useState<SceneId | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await api.catalog(TYPE));
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);

  const rowsOf = (k: string) =>
    (items ?? []).filter((i) => i.key === k).sort((a, b) => b.version - a.version);
  const key = scene ? mbKey(scene) : null;
  const rows = key ? rowsOf(key) : [];
  const live = rows.find((r) => r.enabled) ?? null;
  const spec = scene ? goalSpec(scene) : null;
  const nameOf = (s: SceneId) => t(`student.microbit.scene.${s}.name`);

  function open(s: SceneId, from?: CatalogItem | null) {
    const sp = goalSpec(s);
    const src = from === undefined ? (rowsOf(mbKey(s)).find((r) => r.enabled) ?? null) : from;
    const cfg = (src?.payload ?? null) as MbGoalConfig | null;
    const v = (cfg && checkGoal(s, cfg)) ?? sp.defaults;
    setScene(s);
    setForm({
      s2: toUnit(sp.kind, v[0]),
      s3: toUnit(sp.kind, v[1]),
      l2ko: cfg?.labels?.s2?.ko ?? "",
      l2en: cfg?.labels?.s2?.en ?? "",
      l3ko: cfg?.labels?.s3?.ko ?? "",
      l3en: cfg?.labels?.s3?.en ?? "",
    });
    setNotice(null);
    setFailure(null);
    setServerIssues([]);
  }

  /** 지금 칸 → payload(모양이 틀리면 null) */
  const payloadOf = (): MbGoalConfig | null => {
    if (!scene || !spec || !form) return null;
    const v: [number, number] = [fromUnit(spec.kind, form.s2), fromUnit(spec.kind, form.s3)];
    const labels: { s2?: { ko?: string; en?: string }; s3?: { ko?: string; en?: string } } = {};
    const lab = (ko: string, en: string) => {
      const o: { ko?: string; en?: string } = {};
      if (ko.trim()) o.ko = ko.trim();
      if (en.trim()) o.en = en.trim();
      return Object.keys(o).length ? o : null;
    };
    const s2 = lab(form.l2ko, form.l2en);
    const s3 = lab(form.l3ko, form.l3en);
    if (s2) labels.s2 = s2;
    if (s3) labels.s3 = s3;
    const cfg: MbGoalConfig = { [spec.kind]: v, ...(s2 || s3 ? { labels } : {}) };
    return checkGoal(scene, cfg) ? cfg : null;
  };
  const payload = payloadOf();

  /** 학생이 볼 줄(기본 문구) */
  const lineText = (ms: number) => {
    if (!scene || !spec) return "";
    if (spec.kind === "count") return t(`student.microbit.stars.count.${scene}`, { n: ms });
    if (spec.kind === "hold") return t("student.microbit.stars.hold", { s: ms / 1000 });
    return ms % 60_000 === 0
      ? t("student.microbit.stars.within", { m: ms / 60_000 })
      : t("student.microbit.stars.withinSec", { s: Math.round(ms / 1000) });
  };

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setFailure(null);
    setServerIssues([]);
    try {
      const msg = await fn();
      await load();
      setNotice(msg);
    } catch (e) {
      if (e instanceof ValidationError) setServerIssues(e.issues);
      else if (e instanceof Conflict) setFailure("HTTP-409");
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  const save = (publishAfter: boolean) =>
    run(async () => {
      if (!scene || !key || !payload) return "";
      const v = rows[0] && !rows[0].publishedAt ? rows[0].version : (rows[0]?.version ?? 0) + 1;
      await api.saveCatalog(TYPE, key, v, {
        i18n: { ko: `${nameOf(scene)} 별 기준`, en: `${key} stars` },
        payload: payload as unknown as Json,
      });
      if (publishAfter) await api.publishCatalog(TYPE, key, v);
      return t(publishAfter ? "ops.goals.published" : "ops.goals.saved", { v });
    });

  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => (f ? { ...f, [k]: e.target.value } : f));
  const unit = spec ? t(`ops.goals.mb.unit.${spec.kind}`) : "";

  return (
    <div className="pf-ops-goals" data-testid="mb-goals">
      <StagePicker
        product="mb"
        onProduct={onProduct}
        options={
          items === null
            ? null
            : SCENES.map((sc) => {
                const on = rowsOf(mbKey(sc.id)).find((r) => r.enabled);
                return {
                  key: sc.id,
                  label: `${sc.lesson ?? "MB"} · ${nameOf(sc.id)}`,
                  state: on
                    ? t("ops.stages.stateCustom", { v: on.version })
                    : t("ops.goals.mb.default"),
                };
              })
        }
        value={scene}
        onPick={(k) => open(k as SceneId)}
        testId="mb-goals-pick"
      />
      <section className="pf-pane" aria-labelledby={`${id}-e`}>
        {failure && (
          <ErrorBanner
            cause={t("ops.common.saveFailed")}
            action={t("ops.common.retry")}
            code={failure}
          />
        )}
        {!scene || !spec || !form ? (
          <>
            <h2 id={`${id}-e`} className="pf-h3">
              {t("ops.goals.mb.title")}
            </h2>
            <p className="pf-muted">{t("ops.goals.pick")}</p>
          </>
        ) : (
          <>
            <h2 id={`${id}-e`} className="pf-h3" data-testid="mb-goals-head">
              {SCENES.find((s) => s.id === scene)?.lesson ?? "MB"} · {nameOf(scene)}
            </h2>
            <p className="pf-muted">
              {t(`ops.goals.mb.kind.${spec.kind}`, {
                max: spec.kind === "count" ? spec.max : spec.max / 1000,
              })}
            </p>
            <fieldset className="pf-ops-stages__box">
              <legend>{t("ops.goals.star1")}</legend>
              <p data-testid="mb-goals-star1">
                ⭐ {spec.star1 === null ? t("ops.goals.mb.star1Done") : lineText(spec.star1)} ·{" "}
                <span className="pf-muted">{t("ops.goals.mb.star1Fixed")}</span>
              </p>
            </fieldset>
            {(["2", "3"] as const).map((n) => {
              const val = fromUnit(spec.kind, n === "2" ? form.s2 : form.s3);
              return (
                <fieldset key={n} className="pf-ops-stages__box" data-testid={`mb-goals-s${n}`}>
                  <legend>{t(`ops.goals.star${n}`)}</legend>
                  <div className="pf-ops-stages__form">
                    <label>
                      {t("ops.goals.mb.value")} ({unit}){" "}
                      <input
                        type="number"
                        min={1}
                        step={spec.kind === "count" ? 1 : 5}
                        value={n === "2" ? form.s2 : form.s3}
                        onChange={set(n === "2" ? "s2" : "s3")}
                        data-testid={`mb-goals-s${n}-value`}
                      />
                    </label>
                    <label>
                      {t("ops.goals.labelKo")}{" "}
                      <input
                        value={n === "2" ? form.l2ko : form.l3ko}
                        maxLength={80}
                        size={28}
                        placeholder={Number.isFinite(val) ? lineText(val) : ""}
                        onChange={set(n === "2" ? "l2ko" : "l3ko")}
                      />
                    </label>
                    <label>
                      {t("ops.goals.labelEn")}{" "}
                      <input
                        value={n === "2" ? form.l2en : form.l3en}
                        maxLength={80}
                        size={22}
                        onChange={set(n === "2" ? "l2en" : "l3en")}
                      />
                    </label>
                  </div>
                </fieldset>
              );
            })}
            <p
              className={payload ? "pf-muted" : "pf-ops-stages__warn"}
              role="status"
              data-testid="mb-goals-check"
            >
              {payload
                ? t("ops.goals.ok")
                : t(`ops.goals.mb.bad.${spec.kind}`, {
                    max: spec.kind === "count" ? spec.max : spec.max / 1000,
                  })}
            </p>
            {serverIssues.length > 0 && (
              <ul className="pf-ops-stages__issues">
                {serverIssues.map((i) => (
                  <li key={i.path + i.message}>
                    <code>{i.path}</code> {i.message}
                  </li>
                ))}
              </ul>
            )}
            <div className="pf-row pf-ops-stages__save">
              <Button
                disabled={busy || !payload}
                onClick={() => void save(false)}
                data-testid="mb-goals-save"
              >
                {t("ops.goals.saveDraft")}
              </Button>
              <Button
                variant="accent"
                disabled={busy || !payload}
                onClick={() => void save(true)}
                data-testid="mb-goals-publish"
              >
                {t("ops.goals.publish")}
              </Button>
              {live && (
                <Button
                  variant="ghost"
                  disabled={busy}
                  data-testid="mb-goals-reset"
                  onClick={() =>
                    void run(async () => {
                      await api.disableCatalog(TYPE, key!);
                      open(scene, null);
                      return t("ops.goals.mb.disabled");
                    })
                  }
                >
                  {t("ops.goals.mb.reset")}
                </Button>
              )}
            </div>
            {notice && (
              <p className="pf-note" role="status" data-testid="mb-goals-notice">
                {notice}
              </p>
            )}
            {rows.length > 0 && (
              <table className="pf-tbl" data-testid="mb-goals-versions">
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.version}>
                      <th scope="row">v{r.version}</th>
                      <td>
                        {r.enabled
                          ? t("ops.goals.live")
                          : r.publishedAt
                            ? t("ops.goals.off")
                            : t("ops.goals.draft")}
                        {r.publishedAt ? ` · ${fmtDate(r.publishedAt)}` : ""}
                      </td>
                      <td>
                        <Button variant="ghost" onClick={() => open(scene, r)}>
                          {t("ops.stages.load")}
                        </Button>
                        {!r.enabled && (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            data-testid={`mb-goals-rollback-${r.version}`}
                            onClick={() =>
                              void run(async () => {
                                await api.publishCatalog(TYPE, key!, r.version);
                                return t("ops.goals.rolledBack", { v: r.version });
                              })
                            }
                          >
                            {t("ops.goals.rollback")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="pf-note">{t("ops.goals.mb.note")}</p>
          </>
        )}
      </section>
    </div>
  );
}
